<template>
  <div
    class="plan-card"
    :class="[
      { 'plan-card--current': current },
      { 'plan-card--loading': loading }
    ]"
  >
    <div
      class="plan-card-content"
      :class="{ 'plan-card-content__full': showBenefits }"
    >
      <div class="plan-card__top-row">
        <div class="plan-card__header">
          <div class="plan-card__name">
            {{ planName }}
          </div>
          <div class="plan-card__description">
            {{ planDescription }}
          </div>
        </div>

        <div
          v-if="!hideAction"
          class="plan-card__action"
        >
          <div
            v-if="isCurrent"
            class="current-label"
          >
            Current Plan
          </div>

          <GlButton
            v-else
            :dark="variant === 'primary' && !pendingCancelActionText"
            :pale-light="variant === 'secondary' || !!pendingCancelActionText"
            :title="buttonText"
            @click="onActionClick"
          >
            <template
              v-if="pendingCancelActionText"
              #beforeTitle
            >
              <gl-icon
                gray
                :height="14"
                name="I-Close"
                :width="16"
              />
            </template>
          </GlButton>
        </div>
      </div>

      <div class="plan-card__price-section mt-3">
        <div class="plan-card__price">
          {{ price }}
        </div>
        <div class="plan-card__billing">
          {{ billing }}
        </div>
      </div>
    </div>

    <div
      v-show="showBenefits"
      class="plan-card__benefits"
    >
      <PlanBenefitsList :benefits="benefits" />
    </div>

    <button
      class="plan-card__toggle"
      @click="toggleBenefits"
    >
      <span>{{ toggleLabel }}</span>
      <gl-icon
        class="plan-card__toggle-icon"
        :class="{ 'plan-card__toggle-icon--rotated': showBenefits }"
        gray
        :height="24"
        name="I-ChevronDown"
        :width="24"
      />
    </button>
  </div>
</template>

<script>
// Components
import GlIcon from "@/components/gl-icon.vue";
import PlanBenefitsList from "@/pages/profile/components/PlanBenefitsList.vue";
import GlButton from "@/components/gl-button.vue";

const BUTTON_CONFIG = {
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
  'switch-cycle': {
    annual: 'Switch to Yearly',
    month: 'Switch to Monthly'
  },
  cancel: 'Cancel'
};

export default {
  name: 'PlanCard',
  components: { GlButton, PlanBenefitsList, GlIcon },
  props: {
    id: { type: String, default: '' },
    planName: { type: String, default: 'Plan Name' },
    planDescription: { type: String, default: '' },
    price: { type: String, default: '' },
    billing: { type: String, default: '' },
    benefits: { type: Array, default: () => [] },
    current: { type: Boolean, default: false },
    hideAction: { type: Boolean, default: false },
    actionStatus: { type: String, default: '' },
    selectedType: { type: String, default: 'month' },
    variant: { type: String, default: 'secondary' },
    loading: { type: Boolean, default: false },
    actionLabel: { type: String, default: '' },
    safePlan: { type: Object, default: () => ({}) }
  },
  data: () => ({
    showBenefits: false
  }),
  computed: {
    isCurrent() {
      return this.actionStatus === 'current';
    },

    toggleLabel() {
      return this.showBenefits ? 'Hide all benefits' : 'See all benefits';
    },

    pendingCancelActionText() {
      if (!this.safePlan) return '';

      const { pendingDowngradeCycle, billingCycle, pendingDowngradeTier, planTier } = this.safePlan;
      const isCancelAction = this.actionStatus === 'cancel';
      const targetCycle = pendingDowngradeCycle === 'monthly' ? 'month' : pendingDowngradeCycle;

      if (pendingDowngradeTier) {
        const isSameCycle = !targetCycle || this.selectedType === targetCycle;

        if (isCancelAction || (this.id === pendingDowngradeTier && isSameCycle)) {
          return 'Cancel Downgrade';
        }
      }

      else if (pendingDowngradeCycle && pendingDowngradeCycle !== billingCycle) {
        if (isCancelAction || (this.id === planTier && this.selectedType === targetCycle)) {
          return 'Cancel Switch';
        }
      }

      return '';
    },

    buttonText() {
      if (this.pendingCancelActionText) return this.pendingCancelActionText;

      if (this.actionLabel) return this.actionLabel;

      const config = BUTTON_CONFIG[this.actionStatus];

      if (!config) return 'Select';

      return typeof config === 'object'
          ? config[this.selectedType]
          : config;
    }
  },
  methods: {
    toggleBenefits() {
      this.showBenefits = !this.showBenefits;
    },

    onActionClick() {
      if (this.loading) return;

      if (this.pendingCancelActionText) {
        this.$emit('action', { id: this.id, status: 'cancel' });
        return;
      }

      this.$emit('action', { id: this.id, status: this.actionStatus });
    }
  }
};
</script>

<style scoped>
.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border: 1px solid var(--Basic-200, rgba(9, 21, 64, 0.08));
  border-radius: 12px;
}

.plan-card--current {
  background: rgba(9, 21, 64, 0.04);
}

.plan-card--loading {
  opacity: 0.5;
  cursor: wait;
}

.plan-card-content {
  padding: 12px;
}

.plan-card-content__full {
  border-bottom: 1px solid var(--Basic-200, rgba(9, 21, 64, 0.08));
}

.plan-card__top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.plan-card__header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.plan-card__name {
  font-size: 20px;
  font-weight: 700;
  color: #091540;
}

.plan-card__description {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: rgba(9, 21, 64, 0.68);
}

.plan-card__action {
  flex-shrink: 0;
}

.current-label {
  padding: 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: rgba(9, 21, 64, 0.48);
}

.plan-card__price-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.plan-card__price {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #091540;
}

.plan-card__billing {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: rgba(9, 21, 64, 0.68);
}

.plan-card__benefits {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
}

.plan-card__benefit-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
}

.plan-card__icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.plan-card__text {
  font-size: 14px;
  font-weight: 500;
  color: #091540;
  line-height: 20px;
}

.plan-card__text ::v-deep .value-highlight {
  font-weight: 700;
}

.plan-card__toggle {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #091540;
  gap: 4px;
}

.plan-card__toggle-icon {
  transition: transform 0.3s ease;
}

.plan-card__toggle-icon--rotated {
  transform: rotate(180deg);
}
</style>
